/**
 * Games — enable/disable, stake limits, the payout table, and the house edge.
 *
 * ## The payout table is still not a text box
 *
 * This screen used to say the payout table was read-only because "an editable
 * payout table in an operator panel means the product's mathematics can be
 * changed at 2am by someone with no way to re-derive the edge". That reasoning
 * was right and it has not changed. What has changed is that the operator no
 * longer has to re-derive anything by hand.
 *
 * The raw table stays untouchable. What T-142 adds is one control — a target
 * edge, bounded to 1.00%–8.00% — and the SERVER does the derivation: it
 * recomputes every multiplier from the game's own `distribution()`
 * (`m = (1 − edge) / P(win)`, generalised so a half-loss or a push is held where
 * it is), proves the recomputed table realises the request with
 * `expectedReturn()`, and refuses to write anything it cannot prove. The
 * operator confirms the DIFF, not the number.
 *
 * ## Why the edge could never have been "type it into targetEdge"
 *
 * `games.target_edge` drives nothing. Two readers in the whole server, both
 * reporting. The edge is realised entirely by the multipliers (CLAUDE.md rule
 * 5), so a panel that let someone edit the column would be a panel that let a
 * drifting game be made to look correct by relabelling the target. The column
 * still is not editable; it is now WRITTEN by the recompute, so the metrics and
 * the game cannot disagree.
 *
 * ## Three things this screen has to show, and does
 *
 *   1. **The realised hold beside the target.** A target an operator cannot
 *      compare against reality is half a feature, so the plan carries the hold
 *      from `bets` over the same window the dashboard uses.
 *   2. **Why a locked game is locked.** ADR-009 exempts the table games because
 *      their edges follow from published rules a player can look up. That
 *      sentence is on the screen, not implied by a greyed-out control.
 *   3. **What actually moves.** Every multiplier, before and after; the keys
 *      held because they price a rule; and the stake ceilings the max-win cap
 *      re-derives — because a stale ceiling accepts a bet the game cannot afford
 *      to pay, which is exactly what this morning's Blackjack work found.
 */
import { useState, type ReactNode } from 'react';
import {
  applyEdge,
  bulkSetGamesEnabled,
  cancelEdge,
  fetchBotCoachEnabled,
  fetchBotCoachThresholds,
  fetchBotDifficulty,
  fetchGames,
  fetchLiveness,
  previewEdge,
  setBotCoachEnabled,
  updateBotCoachThresholds,
  updateBotDifficulty,
  updateGame,
  updateLiveness,
  type BotCoachThresholds,
  type BotDifficulty,
  type LivenessSettings,
} from '../api/client.ts';
import type { AdminEdgePlan, AdminGame } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { cmp, formatPercent, fractionToPercent, isAmount, sub } from '../lib/money.ts';

/**
 * The RTP — what a player gets back — as the plain-language complement of the
 * house edge. RTP% = 100 − edge%. A `NUMERIC(18,4)` fraction in, a percent
 * string out, string arithmetic throughout so the fourth place survives.
 */
function rtpPercentFromEdgeFraction(edgeFraction: string): string {
  return sub('100', fractionToPercent(edgeFraction, 4));
}
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  HoldDrift,
  Money,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconAlert, IconCheck, IconDie, IconGauge, IconLock, IconRefresh, IconSliders } from '../ui/icons.tsx';

/** The table's shape, reused by the loading skeleton so nothing jumps. */
const COLUMNS = ['text', 'text', 'text', 'text', 'num', 'num', 'num'] as const;

export function Games(): ReactNode {
  const games = useAsync(() => fetchGames(), []);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const rows = games.data ?? [];
  const active = rows.find((g) => g.slug === selected) ?? null;
  const enabledCount = rows.filter((g) => g.enabled).length;
  const disabledCount = rows.length - enabledCount;

  async function onBulk(enabled: boolean): Promise<void> {
    const targets = rows.filter((g) => g.enabled !== enabled);
    if (targets.length === 0) return;
    const result = await confirm({
      title: enabled ? 'Enable every game' : 'Disable every game',
      intent: enabled ? 'default' : 'danger',
      body: enabled
        ? `Players will be able to stake on all ${targets.length} currently-disabled games immediately.`
        : `New bets are refused on all ${targets.length} currently-enabled games at once. Rounds already open are left to settle — a bet already placed is never voided.`,
      confirmLabel: enabled ? `Enable ${targets.length}` : `Disable ${targets.length}`,
      facts: [
        { label: enabled ? 'Games to enable' : 'Games to disable', value: String(targets.length) },
        { label: 'Each change', value: 'audited individually' },
      ],
      note: { label: 'Reason', required: !enabled },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const outcome = await bulkSetGamesEnabled(targets.map((g) => g.slug), enabled);
      if (outcome.failed.length === 0) {
        toast(`${outcome.changed} game${outcome.changed === 1 ? '' : 's'} ${enabled ? 'enabled' : 'disabled'}.`);
      } else {
        toast(`${outcome.changed} changed, ${outcome.failed.length} failed (${outcome.failed[0]?.slug}: ${outcome.failed[0]?.error}).`, 'error');
      }
      games.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Bulk change failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="split">
      <div className="stack">
        <div className="toolbar">
          <span className="toolbar__note">
            Availability, stake limits and the house edge are operator controls. The raw payout
            table is not — it carries the mathematics and is changed in a reviewed migration.
          </span>
          <button type="button" className="btn btn--sm btn--ghost" onClick={games.reload}>
            <IconRefresh size="sm" />
            Refresh
          </button>
        </div>

        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={busy || disabledCount === 0}
            onClick={() => void onBulk(true)}
          >
            <IconCheck size="sm" />
            Enable all{disabledCount > 0 ? ` (${disabledCount})` : ''}
          </button>
          <button
            type="button"
            className="btn btn--sm btn--danger"
            disabled={busy || enabledCount === 0}
            onClick={() => void onBulk(false)}
          >
            Disable all{enabledCount > 0 ? ` (${enabledCount})` : ''}
          </button>
          <span className="toolbar__note">
            {enabledCount} of {rows.length} live
          </span>
        </div>

        <BotDifficultyCard />
        <AdaptiveCoachCard />
        <LivenessCard />

        <Card title="Games" icon={<IconDie />} count={rows.length} flush>
          {games.error && rows.length === 0 ? (
            <ErrorState
              title="The game catalogue did not load"
              detail={games.error}
              onRetry={games.reload}
            />
          ) : games.loading && rows.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<IconDie size="lg" />}
              title="No games in the catalogue"
              hint="Nothing is registered to take a bet. Games are seeded by migration, not from here."
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Engine</th>
                    <th>State</th>
                    <th className="right">Min stake</th>
                    <th className="right">Max stake</th>
                    <th className="right">House keeps</th>
                    <th className="right">Players get (RTP)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.slug}
                      className={`row--clickable${row.slug === selected ? ' is-selected' : ''}`}
                      onClick={() => setSelected(row.slug)}
                    >
                      <td className="nowrap cell-lead">
                        {row.name}
                        <span className="dim mono" style={{ display: 'block', fontSize: '0.8em' }}>{row.slug}</span>
                      </td>
                      <td className="muted">{row.kind}</td>
                      <td>
                        <Badge tone={row.enabled ? 'win' : 'neutral'}>
                          {row.enabled ? 'enabled' : 'disabled'}
                        </Badge>
                      </td>
                      <td className="right mono">
                        <Money value={row.minStake} />
                      </td>
                      <td className="right mono">
                        <Money value={row.maxStake} />
                      </td>
                      <td className="right mono dim">
                        {formatPercent(fractionToPercent(row.targetEdge))}
                      </td>
                      <td className="right mono">
                        {formatPercent(rtpPercentFromEdgeFraction(row.targetEdge))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <GamePanel game={active} onChanged={games.reload} />
    </div>
  );
}

// --------------------------------------------------- seated bot difficulty (OC1)

const DIFFICULTY_COPY: Record<BotDifficulty, { label: string; blurb: string }> = {
  easy: { label: 'Easy', blurb: 'Blunders often and is nearly blind to danger — what a beginner looks like.' },
  normal: { label: 'Normal', blurb: 'Blunders occasionally; a competent, unremarkable opponent.' },
  sharp: { label: 'Sharp', blurb: 'Always plays its best ranked move.' },
};

/**
 * The global default SKILL of the seated bots (Ludo, Rummy).
 *
 * This is a MOVE-QUALITY lever, not an RNG one: a harder bot makes a better
 * DECISION on the same information — it is never shown more, and the dice and
 * card draws are `crypto.randomInt` at every level (CLAUDE.md rule 5). It writes
 * the `bot_difficulty` platform setting; a follow-up AI layer overrides it
 * per-table / per-player through the server's `resolveBotDifficulty` seam, and
 * this value is the default that seam falls back to.
 */
function BotDifficultyCard(): ReactNode {
  const current = useAsync(() => fetchBotDifficulty(), []);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const value = current.data ?? 'normal';

  async function onSet(next: BotDifficulty): Promise<void> {
    if (next === value) return;
    const result = await confirm({
      title: `Set seated bots to ${DIFFICULTY_COPY[next].label}`,
      body: `${DIFFICULTY_COPY[next].blurb} This changes how well the Ludo and Rummy bots PLAY. It does not touch the dice or the deal — those stay cryptographically random at every level.`,
      confirmLabel: `Set ${DIFFICULTY_COPY[next].label}`,
      facts: [
        { label: 'From', value: DIFFICULTY_COPY[value].label },
        { label: 'To', value: DIFFICULTY_COPY[next].label },
        { label: 'Applies to', value: 'Ludo & Rummy bots (new tables)' },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await updateBotDifficulty(next);
      toast(`Seated bots now play at ${DIFFICULTY_COPY[next].label}.`);
      current.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not change bot difficulty.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Seated bot skill" icon={<IconDie />} sub="How well the Ludo & Rummy bots play — never the dice">
      <p className="field__hint field__hint--block">
        The global default. A harder bot makes a better <em>decision</em> on the same information; it
        is never shown more, and the dice and card draws stay cryptographically random at every
        level. A per-table / per-player policy can override this default; this is what it falls back to.
      </p>
      <div className="row-actions row-actions--start">
        {(['easy', 'normal', 'sharp'] as const).map((level) => (
          <button
            key={level}
            type="button"
            className={`btn btn--sm ${level === value ? 'btn--primary' : 'btn--ghost'}`}
            disabled={busy || current.loading}
            onClick={() => void onSet(level)}
          >
            {level === value ? <IconCheck size="sm" /> : null}
            {DIFFICULTY_COPY[level].label}
          </button>
        ))}
      </div>
      <p className="field__hint">{DIFFICULTY_COPY[value].blurb}</p>
    </Card>
  );
}

const COACH_DEFAULTS: BotCoachThresholds = {
  newcomerGames: 20,
  newcomerAgeDays: 7,
  veteranGames: 100,
  winningNetInr: 5000,
  strugglingNetInr: -2000,
};

const COACH_FIELDS: Array<{ key: keyof BotCoachThresholds; label: string; hint: string }> = [
  { key: 'newcomerGames', label: 'Newcomer under (games)', hint: 'Fewer lifetime seated games than this ⇒ easy — the hook.' },
  { key: 'newcomerAgeDays', label: 'Newcomer under (days)', hint: 'Account younger than this ⇒ easy, regardless of luck.' },
  { key: 'veteranGames', label: 'Veteran at (games)', hint: 'At or above this, a winning player can reach sharp.' },
  { key: 'winningNetInr', label: 'Winning net ≥ (₹)', hint: 'A veteran net at or above this is "winning a lot" ⇒ sharp.' },
  { key: 'strugglingNetInr', label: 'Struggling net ≤ (₹)', hint: 'A net at or below this eases an established player back to easy.' },
];

/**
 * OC2a — the adaptive bot coach. Two operator levers: the ON/OFF (its
 * `ai_config` row; also needs `AI_OPS_ENABLED` on the server) and the ramp
 * thresholds (the `bot_coach` platform setting). Off, or thin data, defers to
 * the global default above — this only ever picks a MOVE-QUALITY tier, never
 * the dice (rule 5).
 */
function AdaptiveCoachCard(): ReactNode {
  const enabled = useAsync(() => fetchBotCoachEnabled(), []);
  const stored = useAsync(() => fetchBotCoachThresholds(), []);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<keyof BotCoachThresholds, string> | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const on = enabled.data ?? false;
  const effective: BotCoachThresholds = { ...COACH_DEFAULTS, ...(stored.data ?? {}) };
  const values = draft ?? (Object.fromEntries(
    COACH_FIELDS.map((f) => [f.key, String(effective[f.key])]),
  ) as Record<keyof BotCoachThresholds, string>);

  async function onToggle(next: boolean): Promise<void> {
    const result = await confirm({
      title: next ? 'Enable the adaptive bot coach' : 'Disable the adaptive bot coach',
      body: next
        ? 'Newcomers face easier bots as a hook; the bots tighten to normal and sharp as a player logs games and wins. This changes only how well the bots PLAY — never the dice or the deal. Needs AI_OPS_ENABLED on the server to take effect.'
        : 'Every table falls back to the global default skill above. No other behaviour changes.',
      confirmLabel: next ? 'Enable coach' : 'Disable coach',
      facts: [{ label: 'Applies to', value: 'Ludo & Rummy bots (new tables)' }],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await setBotCoachEnabled(next);
      toast(next ? 'Adaptive bot coach enabled.' : 'Adaptive bot coach disabled.');
      enabled.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not toggle the coach.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveThresholds(): Promise<void> {
    const parsed: Partial<BotCoachThresholds> = {};
    for (const f of COACH_FIELDS) {
      const n = Number(values[f.key]);
      if (!Number.isFinite(n)) {
        toast(`"${f.label}" must be a number.`, 'error');
        return;
      }
      parsed[f.key] = n;
    }
    setBusy(true);
    try {
      await updateBotCoachThresholds(parsed);
      toast('Coach thresholds saved.');
      setDraft(null);
      stored.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save thresholds.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Adaptive bot coach" icon={<IconSliders />} sub="Bot skill from a player's history — a hook that tightens as they play & win">
      <p className="field__hint field__hint--block">
        When on, a newcomer or a struggling player faces easier bots; the bots tighten to normal and
        sharp as lifetime seated games and net winnings grow. When several humans share a table the
        <em> gentlest</em> tier wins, so a newcomer is protected. It picks a move-quality tier only —
        the dice and card draws stay <code>crypto.randomInt</code> at every tier (rule 5). Off, or on
        thin data, it defers to the global default above.
      </p>
      <div className="row-actions row-actions--start">
        <button
          type="button"
          className={`btn btn--sm ${on ? 'btn--primary' : 'btn--ghost'}`}
          disabled={busy || enabled.loading}
          onClick={() => void onToggle(!on)}
        >
          {on ? <IconCheck size="sm" /> : null}
          {on ? 'Coach ON' : 'Coach OFF'}
        </button>
        <span className="toolbar__note">{on ? 'Deciding per-table from player history' : 'Deferring to the global default'}</span>
      </div>

      <div className="field-grid">
        {COACH_FIELDS.map((f) => (
          <label key={f.key} className="field">
            <span className="field__label">{f.label}</span>
            <input
              className="input"
              type="number"
              value={values[f.key]}
              disabled={busy}
              onChange={(e) =>
                setDraft({ ...values, [f.key]: e.target.value })
              }
            />
            <span className="field__hint">{f.hint}</span>
          </label>
        ))}
      </div>
      <div className="row-actions row-actions--start">
        <button
          type="button"
          className="btn btn--sm btn--primary"
          disabled={busy || draft === null}
          onClick={() => void onSaveThresholds()}
        >
          Save thresholds
        </button>
        {draft !== null ? (
          <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => setDraft(null)}>
            Reset
          </button>
        ) : null}
      </div>
    </Card>
  );
}

function GamePanel({
  game,
  onChanged,
}: {
  game: AdminGame | null;
  onChanged: () => void;
}): ReactNode {
  const [minStake, setMinStake] = useState('');
  const [maxStake, setMaxStake] = useState('');
  const [maxPayout, setMaxPayout] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  if (!game) {
    return (
      <Card title="Game settings" icon={<IconSliders />}>
        <EmptyState
          compact
          icon={<IconSliders size="lg" />}
          title="No game selected"
          hint="Pick a row to see its stake limits and the payout table that realises its edge."
        />
      </Card>
    );
  }

  // Reset the draft when the selection changes, without an effect: the selected
  // slug is the identity of the form.
  if (editing !== game.slug) {
    setEditing(game.slug);
    setMinStake(game.minStake);
    setMaxStake(game.maxStake);
    setMaxPayout(game.maxPayout ?? '');
  }

  const minValid = isAmount(minStake);
  const maxValid = isAmount(maxStake);
  const payoutValid = isAmount(maxPayout) && cmp(maxPayout, minStake) >= 0;
  const orderValid = minValid && maxValid && payoutValid && cmp(minStake, maxStake) < 0;
  const dirty =
    minStake !== game.minStake || maxStake !== game.maxStake || maxPayout !== (game.maxPayout ?? '');

  async function onToggle(): Promise<void> {
    if (!game) return;
    const next = !game.enabled;
    const result = await confirm({
      title: next ? 'Enable this game' : 'Disable this game',
      intent: next ? 'default' : 'danger',
      body: next
        ? 'Players will be able to stake on this game immediately.'
        : 'New bets are refused at once. Rounds already open are left to settle — disabling does not void a bet a player has already placed.',
      confirmLabel: next ? 'Enable' : 'Disable',
      facts: [
        { label: 'Game', value: game.name },
        { label: 'Slug', value: game.slug },
      ],
      note: { label: 'Reason', required: !next },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updateGame(game.slug, { enabled: next });
      toast(`${game.name} is now ${next ? 'enabled' : 'disabled'}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the game.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveLimits(): Promise<void> {
    if (!game || !orderValid) return;
    const result = await confirm({
      title: 'Change stake limits',
      body: 'Limits apply to new bets from the moment they are saved.',
      confirmLabel: 'Save limits',
      facts: [
        { label: 'Game', value: game.name },
        {
          label: 'Min stake',
          value: (
            <>
              <Money value={game.minStake} /> → <Money value={minStake} />
            </>
          ),
        },
        {
          label: 'Max stake',
          value: (
            <>
              <Money value={game.maxStake} /> → <Money value={maxStake} />
            </>
          ),
        },
        {
          label: 'Max win per bet',
          value: (
            <>
              {game.maxPayout === null ? 'not set' : <Money value={game.maxPayout} />} →{' '}
              <Money value={maxPayout} />
            </>
          ),
        },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updateGame(game.slug, { minStake, maxStake, maxPayout });
      toast(`Limits updated for ${game.name}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the limits.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card title={game.name} icon={<IconDie />}>
        <div className="kv kv--spaced">
          <span className="kv__k">Slug</span>
          <span className="kv__v num">{game.slug}</span>
          <span className="kv__k">Engine</span>
          <span className="kv__v">{game.kind}</span>
          <span className="kv__k">House keeps</span>
          <span className="kv__v">{formatPercent(fractionToPercent(game.targetEdge))}</span>
          <span className="kv__k">Players get (RTP)</span>
          <span className="kv__v">{formatPercent(rtpPercentFromEdgeFraction(game.targetEdge))}</span>
          <span className="kv__k">Max win per bet</span>
          <span className="kv__v">
            {game.maxPayout === null ? 'not set' : <Money value={game.maxPayout} />}
          </span>
        </div>

        <label className="field">
          <span className="field__label">Minimum stake</span>
          <input
            className="input num"
            value={minStake}
            onChange={(event) => setMinStake(event.target.value)}
          />
          {!minValid ? <span className="field__error">Decimal, up to four places.</span> : null}
        </label>

        <label className="field">
          <span className="field__label">Maximum stake</span>
          <input
            className="input num"
            value={maxStake}
            onChange={(event) => setMaxStake(event.target.value)}
          />
          {!maxValid ? (
            <span className="field__error">Decimal, up to four places.</span>
          ) : minValid && maxValid && cmp(minStake, maxStake) >= 0 ? (
            <span className="field__error">The maximum must be above the minimum.</span>
          ) : null}
        </label>

        {/*
          ADR-074. The other half of the sentence the stake limits start: what a
          bet may be, and what it may pay. It moves no multiplier and no
          probability — the engine spends it BEFORE the wager, by refusing a
          stake whose dearest outcome would exceed it, so a player is never told
          after a draw that their win was trimmed.
        */}
        <label className="field">
          <span className="field__label">Maximum win per bet</span>
          <input
            className="input num"
            value={maxPayout}
            onChange={(event) => setMaxPayout(event.target.value)}
          />
          <span className="field__hint">
            The most one bet on this game may return, total, in INR. It caps the payout, never the
            draw: a stake whose best outcome would pay more than this is refused at the moment it is
            placed, naming the largest stake that fits.
          </span>
          {!isAmount(maxPayout) ? (
            <span className="field__error">Decimal, up to four places.</span>
          ) : !payoutValid ? (
            <span className="field__error">
              Below the minimum stake, no bet on this game could be placed at all.
            </span>
          ) : null}
        </label>

        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy || !dirty || !orderValid}
            onClick={onSaveLimits}
          >
            <IconSliders size="sm" />
            Save limits
          </button>
          <button
            type="button"
            className={`btn btn--sm ${game.enabled ? 'btn--danger' : 'btn--primary'}`}
            disabled={busy}
            onClick={onToggle}
          >
            {game.enabled ? 'Disable game' : 'Enable game'}
          </button>
        </div>
      </Card>

      <HouseEdgeCard slug={game.slug} onChanged={onChanged} />

      <Card title="Payout table" icon={<IconLock />} actions={<Badge tone="neutral">read-only</Badge>}>
        <p className="field__hint field__hint--block">
          The edge is realised by this table. Changing it changes the mathematics of the game, so it
          is edited in a reviewed migration with the edge re-derived — never from here.
        </p>
        <pre className="well">{JSON.stringify(game.payoutTable, null, 2)}</pre>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------ the house edge

/**
 * A percentage the operator typed, as the fraction the API takes.
 *
 * String arithmetic, no float, for the same reason every amount in this panel is
 * a string: `0.0499` parsed to a double and printed back is not reliably
 * `0.0499`, and the fourth place of an edge is a real quarter of a basis point.
 *
 * A percentage with at most two decimals IS a fraction with at most four —
 * `pct / 100`, a shift of two places — so the conversion is a pad and a prefix.
 * Two places is the deliberate limit: the column is `NUMERIC(18,4)`, so a third
 * would be silently truncated by Postgres into a target nobody was shown.
 */
export function percentToFraction(pct: string): string | null {
  const trimmed = pct.trim();
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole = '0', frac = ''] = trimmed.split('.');
  const hundredths = `${whole}${frac.padEnd(2, '0')}`;
  return `0.${hundredths.padStart(4, '0')}`;
}

/**
 * OC2b (ADR-060) — house-bot lobby liveness. House-funded, `is_bot`-flagged
 * accounts place REAL bets in house-banked games so the tables and the wins feed
 * are not empty at launch. This card is the ONE switch and its levers.
 *
 * The honest framing, stated on the card: these are REAL bets with REAL
 * `crypto.randomInt` outcomes (rule 5 — never adjusted), funded from HOUSE float
 * (not a deposit, not a liability), and every bot is flagged so the owner's
 * analytics EXCLUDE it. Nothing here can fool the business numbers; it only
 * seeds the room. `showInFeed` decides whether a bot's masked win may show in
 * the public feed.
 */
function LivenessCard(): ReactNode {
  const current = useAsync(() => fetchLiveness(), []);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const v = current.data;

  async function save(next: LivenessSettings, verb: string): Promise<void> {
    setBusy(true);
    try {
      await updateLiveness(next);
      toast(verb);
      current.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update liveness.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onToggle(): Promise<void> {
    if (!v) return;
    const turningOn = !v.enabled;
    const result = await confirm({
      title: turningOn ? 'Enable house-bot liveness' : 'Disable house-bot liveness',
      body: turningOn
        ? 'House-funded bots will place REAL bets in house-banked games so the tables and the wins feed are not empty. Outcomes are ordinary crypto.randomInt — never adjusted for a bot. The money is HOUSE float, not a deposit and not a liability, and every bot is flagged so your analytics exclude it.'
        : 'Bots stop placing bets immediately. Existing bot accounts and their house float are left as they are.',
      confirmLabel: turningOn ? 'Enable' : 'Disable',
      facts: [
        { label: 'Bots', value: String(v.botCount) },
        { label: 'Stake range', value: `₹${v.stakeMinInr}–₹${v.stakeMaxInr}` },
        { label: 'Games', value: v.games.join(', ') || '(none)' },
        { label: 'Show wins in feed', value: v.showInFeed ? 'yes' : 'no' },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    await save({ ...v, enabled: turningOn }, turningOn ? 'House-bot liveness enabled.' : 'House-bot liveness disabled.');
  }

  return (
    <Card title="Lobby liveness (house bots)" icon={<IconSliders />} sub="Real house-funded bets so tables & feeds aren't empty — flagged, analytics-excluded">
      <p className="field__hint field__hint--block">
        House-funded, flagged accounts place <em>real</em> bets in house-banked games. Outcomes are
        ordinary <code>crypto.randomInt</code>, never adjusted for a bot (rule 5); the money is house
        float, not a deposit; and every bot is excluded from your GGR, turnover, actives and whale
        list. One switch below; disabled means zero bot activity.
      </p>

      {current.loading || !v ? (
        <p className="field__hint">Loading…</p>
      ) : (
        <>
          <div className="row-actions row-actions--start">
            <button
              type="button"
              className={`btn btn--sm ${v.enabled ? 'btn--danger' : 'btn--primary'}`}
              disabled={busy}
              onClick={() => void onToggle()}
            >
              {v.enabled ? 'Disable liveness' : 'Enable liveness'}
            </button>
            <span className="toolbar__note">{v.enabled ? 'ON — bots are betting' : 'OFF — no bot activity'}</span>
          </div>

          <div className="row-actions row-actions--start" style={{ marginTop: 8 }}>
            <button
              type="button"
              className={`btn btn--sm ${v.showInFeed ? 'btn--primary' : 'btn--ghost'}`}
              disabled={busy}
              onClick={() => void save({ ...v, showInFeed: !v.showInFeed }, v.showInFeed ? 'Bot wins hidden from the feed.' : 'Bot wins shown in the feed (masked).')}
            >
              {v.showInFeed ? <IconCheck size="sm" /> : null}
              Show bot wins in feed
            </button>
          </div>

          <p className="field__hint">
            {v.botCount} bots · stakes ₹{v.stakeMinInr}–₹{v.stakeMaxInr} · {v.betsPerTick} bets/tick ·
            games: {v.games.join(', ') || '(none)'}. Bot money is house float, not a player liability.
          </p>
        </>
      )}
    </Card>
  );
}

const MODEL_LABEL: Record<string, string> = {
  multipliers: 'priced per bet',
  hold: 'one hold factor',
  'house-factor': 'one house factor',
  locked: 'locked',
  unsupported: 'not editable',
};

function HouseEdgeCard({ slug, onChanged }: { slug: string; onChanged: () => void }): ReactNode {
  const [target, setTarget] = useState('');
  const [plan, setPlan] = useState<AdminEdgePlan | null>(null);
  const [preview, setPreview] = useState<AdminEdgePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  // The selected slug is the identity of this card, so the draft resets with it
  // rather than through an effect. A preview belonging to another game on screen
  // beside this one's name is the worst thing this card could do.
  if (loadedFor !== slug) {
    setLoadedFor(slug);
    setPlan(null);
    setPreview(null);
    setTarget('');
    setError(null);
    void previewEdge(slug)
      .then((next) => {
        setPlan(next);
        setTarget(fractionToPercentInput(next.recordedEdgePct));
      })
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Could not read the edge.'));
  }

  if (error !== null) {
    return (
      <Card title="House edge" icon={<IconGauge />}>
        <p className="field__hint field__hint--block">{error}</p>
      </Card>
    );
  }
  if (!plan) {
    return (
      <Card title="House edge" icon={<IconGauge />}>
        <TableSkeleton columns={['text', 'num', 'num']} rows={3} />
      </Card>
    );
  }

  const fraction = percentToFraction(target);
  const inBounds =
    fraction !== null &&
    cmp(fractionToPercent(fraction, 4), plan.minEdgePct) >= 0 &&
    cmp(fractionToPercent(fraction, 4), plan.maxEdgePct) <= 0;

  async function onPreview(): Promise<void> {
    if (!fraction || !inBounds) return;
    setBusy(true);
    try {
      setPreview(await previewEdge(slug, fraction));
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not recompute.', 'error');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The confirmation restates the DIFF — every multiplier that moves, or the one
   * constant — and never just the number. The `planToken` sent with it is the
   * fingerprint of the exact table that was on screen, so if the recompute has
   * moved underneath the operator the server refuses with a 409 and the new diff
   * has to be read first.
   */
  async function onApply(): Promise<void> {
    if (!preview || !preview.verification.ok) return;
    const facts = preview.constant
      ? [
          { label: 'Game', value: preview.name },
          { label: preview.constant.key, value: `${preview.constant.from} → ${preview.constant.to}` },
          { label: 'Bets verified', value: String(preview.selectionCount) },
        ]
      : [
          { label: 'Game', value: preview.name },
          { label: 'Multipliers moving', value: String(preview.changes.length) },
          ...preview.changes.slice(0, 8).map((c) => ({
            label: c.key,
            value: `${c.from} → ${c.to}`,
          })),
          ...(preview.changes.length > 8
            ? [{ label: '…', value: `and ${preview.changes.length - 8} more, listed on the screen behind this` }]
            : []),
        ];

    const result = await confirm({
      title: `Set ${preview.name} to ${formatPercent(preview.requestedEdgePct, { places: 4 })}`,
      intent: 'danger',
      body:
        preview.kind === 'round'
          ? 'This takes effect at the NEXT round. Every bet on a round already open keeps the price it was placed at — a round’s outcome is sealed when it locks, and repricing mid-round would change what an already-placed bet pays.'
          : 'This game has no shared round, so it takes effect immediately. A bet here is staked, drawn and settled inside one transaction, so there is no interval in which a price could move under one.',
      confirmLabel: 'Apply this diff',
      facts,
      note: { label: 'Reason', required: true, hint: 'Recorded in the audit log with the whole table, before and after.' },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const applied = await applyEdge(slug, percentToFraction(target) as string, preview.planToken, result.note);
      toast(
        applied.status === 'staged'
          ? `${preview.name} is set to ${formatPercent(applied.edgePct, { places: 4 })} from the next round.`
          : `${preview.name} is now at ${formatPercent(applied.edgePct, { places: 4 })}.`,
      );
      setPreview(null);
      setPlan(await previewEdge(slug));
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not apply the change.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(scope: 'pending' | 'override'): Promise<void> {
    const result = await confirm({
      title: scope === 'pending' ? 'Drop the staged change' : 'Hand this game back to the seed',
      body:
        scope === 'pending'
          ? 'It never reached a round boundary, so no price ever moved and nothing is undone.'
          : 'The current prices stay exactly as they are — repricing a live game is what the round boundary is for. What changes is ownership: the next deploy restores the catalogue’s own table.',
      confirmLabel: scope === 'pending' ? 'Drop it' : 'Release',
      facts: [{ label: 'Game', value: plan?.name ?? slug }],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      setPlan(await cancelEdge(slug, scope));
      setPreview(null);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not change it.', 'error');
    } finally {
      setBusy(false);
    }
  }

  const realisedRange = edgeRange(plan);

  return (
    <Card
      title="House edge"
      icon={<IconGauge />}
      actions={
        <>
          {plan.pendingEdgePct !== null ? (
            <Badge tone="pending">staged {formatPercent(plan.pendingEdgePct, { places: 4 })}</Badge>
          ) : null}
          {plan.overridden ? <Badge tone="info">operator-set</Badge> : null}
          <Badge tone={plan.editable ? 'neutral' : 'loss'}>{MODEL_LABEL[plan.model] ?? plan.model}</Badge>
        </>
      }
    >
      <p className="field__hint field__hint--block">
        <strong>
          House keeps {formatPercent(plan.recordedEdgePct)} — players get{' '}
          {formatPercent(sub('100', plan.recordedEdgePct))} back (RTP)
        </strong>
        . This is the legitimate win-rate lever: the edge is realised entirely by the payout
        multipliers, never by nudging an outcome (rule 5). Set a target below and confirm the
        re-priced table.
      </p>

      <div className="kv kv--spaced">
        <span className="kv__k">Recorded target (house edge)</span>
        <span className="kv__v num">{formatPercent(plan.recordedEdgePct, { places: 4 })}</span>
        <span className="kv__k">Players get back (RTP)</span>
        <span className="kv__v num">{formatPercent(sub('100', plan.recordedEdgePct), { places: 4 })}</span>
        <span className="kv__k">The table realises</span>
        <span className="kv__v num">{realisedRange}</span>
        <span className="kv__k">Realised hold ({plan.betCount} bets, 24h)</span>
        <span className="kv__v num">
          {plan.realisedHoldPct === null ? '—' : formatPercent(plan.realisedHoldPct)}
        </span>
        <span className="kv__k">Target against reality</span>
        <span className="kv__v">
          <HoldDrift realised={plan.realisedHoldPct} configured={fractionToPercent(plan.recordedEdgePct, 2)} />
        </span>
      </div>

      {!plan.editable ? (
        <p className="field__hint field__hint--block">
          <IconLock size="sm" /> {plan.reason}
        </p>
      ) : (
        <>
          <label className="field">
            <span className="field__label">
              Target house edge, %
              {fraction !== null && inBounds ? (
                <span className="dim"> — players would get {formatPercent(sub('100', fractionToPercent(fraction, 4)))} back</span>
              ) : null}
            </span>
            <input
              type="range"
              className="input"
              min={Number(plan.minEdgePct)}
              max={Number(plan.maxEdgePct)}
              step={0.25}
              value={fraction !== null ? Number(fractionToPercent(fraction, 2)) : Number(plan.minEdgePct)}
              onChange={(event) => {
                setTarget(Number(event.target.value).toFixed(2));
                setPreview(null);
              }}
            />
            <input
              className="input num"
              value={target}
              placeholder="4.00"
              onChange={(event) => {
                setTarget(event.target.value);
                setPreview(null);
              }}
            />
            <span className="field__hint">
              Between {plan.minEdgePct}% and {plan.maxEdgePct}%, to two decimal places. Every multiplier is
              recomputed from this game&rsquo;s own arithmetic and the result is verified before anything is
              written.
            </span>
            {target !== '' && fraction === null ? (
              <span className="field__error">A percentage with at most two decimal places.</span>
            ) : target !== '' && !inBounds ? (
              <span className="field__error">
                Out of bounds. The panel accepts {plan.minEdgePct}% to {plan.maxEdgePct}%.
              </span>
            ) : null}
          </label>

          <div className="row-actions row-actions--start">
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy || !inBounds}
              onClick={onPreview}
            >
              <IconRefresh size="sm" />
              Recompute
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busy || !preview || !preview.verification.ok}
              onClick={onApply}
            >
              <IconCheck size="sm" />
              Apply this diff
            </button>
            {plan.pendingEdgePct !== null ? (
              <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={() => void onCancel('pending')}>
                Drop the staged change
              </button>
            ) : null}
            {plan.overridden ? (
              <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void onCancel('override')}>
                Hand back to the seed
              </button>
            ) : null}
          </div>

          {preview ? <EdgeDiff plan={preview} /> : null}
        </>
      )}
    </Card>
  );
}

/** What the table realises today, as a single reading: one figure, or a range. */
function edgeRange(plan: AdminEdgePlan): string {
  if (plan.currentEdges.length === 0) return '—';
  let low = plan.currentEdges[0]?.edgePct ?? '0';
  let high = low;
  for (const entry of plan.currentEdges) {
    if (cmp(entry.edgePct, low) < 0) low = entry.edgePct;
    if (cmp(entry.edgePct, high) > 0) high = entry.edgePct;
  }
  return low === high
    ? formatPercent(low, { places: 4 })
    : `${formatPercent(low, { places: 4 })} – ${formatPercent(high, { places: 4 })}`;
}

/**
 * The diff. This — not the number in the box — is what the operator is agreeing
 * to, so it shows every multiplier that moves, what is deliberately held, what
 * each bet would then realise, and the stake ceilings that move with it.
 */
function EdgeDiff({ plan }: { plan: AdminEdgePlan }): ReactNode {
  return (
    <div className="stack">
      {plan.verification.ok ? (
        <p className="field__hint field__hint--block">
          <IconCheck size="sm" /> Verified: all {plan.selectionCount} priced bets realise{' '}
          {formatPercent(plan.requestedEdgePct, { places: 4 })}. Measured band{' '}
          {formatPercent(plan.verification.bestEdgePct, { places: 4 })} –{' '}
          {formatPercent(plan.verification.worstEdgePct, { places: 4 })}; never below the target, and never
          further above it than the {plan.verification.truncationBoundPct}pp that truncating a multiplier to
          four places can account for.
        </p>
      ) : (
        <div className="well">
          <p className="field__hint field__hint--block">
            <IconAlert size="sm" /> This recompute does NOT realise{' '}
            {formatPercent(plan.requestedEdgePct, { places: 4 })}, so it cannot be saved.
          </p>
          <ul>
            {plan.verification.failures.slice(0, 6).map((failure) => (
              <li key={failure}>{failure}</li>
            ))}
          </ul>
        </div>
      )}

      {plan.constant ? (
        <div className="kv kv--spaced">
          <span className="kv__k">params.{plan.constant.key}</span>
          <span className="kv__v num">
            {plan.constant.from} → {plan.constant.to}
          </span>
          <span className="kv__k">Prices derived from it</span>
          <span className="kv__v num">{plan.selectionCount}</span>
        </div>
      ) : null}

      {plan.changes.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Payout key</th>
                <th className="right">Now</th>
                <th className="right">After</th>
                <th>Bets it prices</th>
              </tr>
            </thead>
            <tbody>
              {plan.changes.map((change) => (
                <tr key={change.key}>
                  <td className="mono nowrap">{change.key}</td>
                  <td className="right mono dim">{change.from}</td>
                  <td className="right mono">{change.to}</td>
                  <td className="muted">{change.selections.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {plan.heldKeys.length > 0 ? (
        <p className="field__hint field__hint--block">
          Held exactly where they are, because they price a rule rather than a bet — a push, a half-loss:{' '}
          <span className="mono">{plan.heldKeys.join(', ')}</span>.
        </p>
      ) : null}

      {plan.ceilings.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Bet</th>
                <th className="right">Best case</th>
                <th className="right">Max stake, re-derived</th>
              </tr>
            </thead>
            <tbody>
              {plan.ceilings.map((ceiling) => (
                <tr key={ceiling.label}>
                  <td className="mono nowrap">{ceiling.label}</td>
                  <td className="right mono">
                    <span className="dim">{ceiling.maxReturnMultipleBefore}</span> → {ceiling.maxReturnMultipleAfter}
                  </td>
                  <td className="right mono">
                    <Money value={ceiling.maxStakeBefore} /> → <Money value={ceiling.maxStakeAfter} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {plan.maxReturn !== null ? (
        <p className="field__hint field__hint--block">
          Ceilings are the payout cap <span className="mono">params.maxReturn</span> ={' '}
          <Money value={plan.maxReturn} /> divided by each bet&rsquo;s best case. A raised price tightens its
          ceiling; a stale one would accept a bet the game cannot afford to pay.
        </p>
      ) : null}
    </div>
  );
}

/** A stored percentage string ("3.7300") as the two-place figure the input takes. */
function fractionToPercentInput(percentString: string): string {
  const units = percentString.split('.');
  const whole = units[0] ?? '0';
  const frac = (units[1] ?? '').padEnd(2, '0').slice(0, 2);
  return `${whole}.${frac}`;
}
